import * as XLSX from "xlsx";
import type { TelanganaDistrictKey } from "../data/telanganaDistricts";
import { isTelanganaDistrictKey } from "../data/telanganaDistricts";
import type { LeadParseStats, ParsedLeadRow } from "./leadsImportParse";
import { districtLabelToKey, districtLineToKey, parseAppCellFlexible } from "./leadsImportParse";

function normHeader(s: unknown): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function findCol(headers: string[], ...candidates: string[]): number {
  const h = headers.map(normHeader);
  for (const c of candidates) {
    const n = normHeader(c);
    const i = h.findIndex((x) => x === n || x.includes(n) || n.includes(x));
    if (i >= 0) return i;
  }
  return -1;
}

/** Exact header “District” only — avoids substring false positives (e.g. “constituency”). */
function findDistrictCol(headers: string[]): number {
  const h = headers.map(normHeader);
  const exact = [
    "district",
    "district name",
    "name of district",
    "name of the district",
    "dist",
    "dist name",
    "name of dist",
    "name of the dist",
  ];
  for (const ex of exact) {
    const i = h.findIndex((x) => x === ex);
    if (i >= 0) return i;
  }
  return -1;
}

function findSlNoCol(headers: string[]): number {
  const h = headers.map(normHeader);
  const exact = ["sl no", "s.no", "slno", "serial no", "serial number", "s no"];
  for (const ex of exact) {
    const i = h.findIndex((x) => x === ex);
    if (i >= 0) return i;
  }
  return findCol(headers, "sl no", "serial");
}

/** Parse first sheet of an XLSX workbook with TG-style columns. */
export function parseLeadExcelWorkbook(
  data: ArrayBuffer,
  opts?: { defaultDistrictKey?: TelanganaDistrictKey | null },
): {
  districtKey: TelanganaDistrictKey | null;
  rows: ParsedLeadRow[];
  stats: LeadParseStats;
} {
  const wb = XLSX.read(data, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const emptyStats = (lineCount: number): LeadParseStats => ({
    fileLineCount: lineCount,
    rowsAccepted: 0,
    skippedNoAppToken: 0,
    skippedNoMandalVillage: 0,
    skippedShortApplicant: 0,
    skippedRequiredBlank: 0,
    looseLineFallbacks: 0,
  });

  if (!sheet) return { districtKey: null, rows: [], stats: emptyStats(0) };
  const matrix = XLSX.utils.sheet_to_json<(string | number | null | undefined)[]>(sheet, {
    header: 1,
    defval: "",
    raw: false,
  }) as string[][];

  let headerRowIdx = -1;
  const headers: string[] = [];
  for (let i = 0; i < Math.min(matrix.length, 30); i++) {
    const row = matrix[i] ?? [];
    const joined = row.map((c) => String(c ?? "")).join(" ");
    if (/district\s*[-–]/i.test(joined)) {
      const dk = districtLineToKey(joined);
      if (dk) {
        /* district row — not header */
      }
    }
    const lower = joined.toLowerCase();
    if (lower.includes("mandal") && lower.includes("village") && (lower.includes("applicant") || lower.includes("name"))) {
      headerRowIdx = i;
      for (const c of row) headers.push(String(c ?? ""));
      break;
    }
  }

  if (headerRowIdx < 0) return { districtKey: null, rows: [], stats: emptyStats(matrix.length) };

  const iMandal = findCol(headers, "name of the mandal", "mandal");
  const iVillage = findCol(headers, "village name", "village");
  /** Prefer explicit “Application No” so we do not treat S.No as part of the application number. */
  const iApp = findCol(headers, "application no", "application number", "sno application", "application");
  const iName = findCol(headers, "applicant name", "applicant");
  const iMobile = findCol(headers, "mobile");
  const iAadhaar = findCol(headers, "aadhaar");
  const iList = findCol(headers, "list type", "initial list");
  const iStatus = findCol(headers, "status", "application status");
  const iDistrict = findDistrictCol(headers);
  const iSl = findSlNoCol(headers);

  if (iMandal < 0 || iVillage < 0 || iApp < 0 || iName < 0) {
    return { districtKey: null, rows: [], stats: emptyStats(matrix.length) };
  }

  let documentDistrictKey: TelanganaDistrictKey | null = opts?.defaultDistrictKey ?? null;
  let currentMandal = "";
  let currentVillage = "";
  const rows: ParsedLeadRow[] = [];
  const stats: LeadParseStats = {
    fileLineCount: matrix.length,
    rowsAccepted: 0,
    skippedNoAppToken: 0,
    skippedNoMandalVillage: 0,
    skippedShortApplicant: 0,
    skippedRequiredBlank: 0,
    looseLineFallbacks: 0,
  };

  for (let r = headerRowIdx + 1; r < matrix.length; r++) {
    const excelRow = matrix[r] ?? [];
    const joined = excelRow.map((c) => String(c ?? "").trim()).join(" ");
    const dk = districtLineToKey(joined) ?? districtLineToKey(String(excelRow[0] ?? ""));
    if (dk) {
      documentDistrictKey = dk;
      continue;
    }

    const cell = (i: number) => (i >= 0 ? String(excelRow[i] ?? "").trim() : "");
    let fm = cell(iMandal);
    let fv = cell(iVillage);
    const appCell = cell(iApp);
    const name = cell(iName);
    const mobile = (iMobile >= 0 ? cell(iMobile) : "").replace(/\D/g, "").slice(0, 15);
    const aadhaar = (iAadhaar >= 0 ? cell(iAadhaar) : "").replace(/\D/g, "").slice(0, 12);
    const listType = iList >= 0 ? cell(iList) : "";
    const applicationStatus = iStatus >= 0 ? cell(iStatus) : "";
    const districtCell = iDistrict >= 0 ? cell(iDistrict) : "";
    const rowDistrictKey = districtCell ? districtLabelToKey(districtCell) : null;
    const slCell = iSl >= 0 ? cell(iSl) : "";
    const slDigits = slCell.replace(/\D/g, "");

    if (!fm && !fv && !appCell && !name) continue;

    const parsedApp = parseAppCellFlexible(appCell);
    const applicationRef = parsedApp?.applicationRef ?? appCell.replace(/^\d+\s+/, "").trim();
    const applicationSerial =
      slDigits && slDigits.length > 0 ? slDigits : parsedApp?.serial ?? "0";

    if (fm) currentMandal = fm;
    if (fv) currentVillage = fv;
    if (!parsedApp && !applicationRef) {
      stats.skippedNoAppToken += 1;
      continue;
    }
    if (!name || name.length < 1) {
      stats.skippedShortApplicant += 1;
      continue;
    }
    if (!mobile || !aadhaar || !listType || !applicationStatus || !(rowDistrictKey || documentDistrictKey || opts?.defaultDistrictKey)) {
      stats.skippedRequiredBlank += 1;
      continue;
    }
    if (!currentMandal || !currentVillage) {
      stats.skippedNoMandalVillage += 1;
      continue;
    }

    const parsedRow: ParsedLeadRow = {
      fileMandal: currentMandal,
      fileVillage: currentVillage,
      applicantName: name,
      mobile,
      aadhaarDigits: aadhaar,
      applicationRef: applicationRef || appCell,
      applicationSerial,
      listType,
      applicationStatus,
    };
    if (rowDistrictKey && isTelanganaDistrictKey(rowDistrictKey)) {
      parsedRow.fileDistrictKey = rowDistrictKey;
    }
    rows.push(parsedRow);
  }

  stats.rowsAccepted = rows.length;

  const keysFromRows = rows
    .map((r) => r.fileDistrictKey)
    .filter((k): k is TelanganaDistrictKey => !!(k && isTelanganaDistrictKey(k)));
  let aggregateDistrict: TelanganaDistrictKey | null = documentDistrictKey ?? opts?.defaultDistrictKey ?? null;
  if (keysFromRows.length === rows.length && rows.length > 0) {
    const k0 = keysFromRows[0]!;
    aggregateDistrict = keysFromRows.every((k) => k === k0) ? k0 : null;
  }

  return { districtKey: aggregateDistrict, rows, stats };
}
